export function actionCue(type) {
  if (type === 'CHECK') return 'turn';
  if (type === 'FOLD') return 'fold';
  if (['CALL', 'BET', 'RAISE', 'ALL_IN'].includes(type)) return 'chip';
  return null;
}

/** Normal hands have no automatic sound sequence. Only one direct human
 * action cue and the one-time robbery scene are allowed through. */
export function tableSoundFor({ robbery = false, playerInitiated = false, cue = null } = {}) {
  if (robbery) return 'robbery';
  return playerInitiated ? cue : null;
}

/** Scene sounds are edges, not persistent state. */
export function tableSoundForTransition(previous, next, { playerInitiated = false, cue = null } = {}) {
  const robbery = Boolean(next?.robbery && !previous?.robbery);
  return tableSoundFor({ robbery, playerInitiated, cue });
}

export function atmosphereFor(state) {
  if (state.seats[0].stack < state.hardTimesBalance) return { id: 'hard-times', music: 'hard' };
  const presentationPot = Math.max(state.pot || 0, resultPot(state));
  if (presentationPot >= state.extremePot) return { id: 'pot-extreme', music: 'high' };
  if (presentationPot >= Math.round(state.buyIn * 0.35)) return { id: 'pot-high', music: 'high' };
  if (presentationPot >= Math.round(state.buyIn * 0.10)) return { id: 'pot-warm', music: 'base' };
  return { id: 'pot-calm', music: 'base' };
}

function resultPot(state) {
  const match = state.result?.detail?.match(/\$([\d,]+)(?:\.(\d{2}))?/);
  if (!match) return 0;
  return Number(match[1].replaceAll(',', '')) * 100 + Number(match[2] || 0);
}

/**
 * A single celebratory tabletop prop is reserved for occasional consequential
 * hands. The prop reflects the player's current wealth instead of rotating at
 * random, so the felt remains quiet most of the time and the richer objects
 * feel earned.
 */
export function tabletopPropFor(state) {
  const player = state.seats?.[0];
  if (!player || player.stack < state.hardTimesBalance) return null;
  const threshold = Math.max(Math.round(state.buyIn * 0.35), Math.round(state.ante * 12));
  const qualifyingPot = Math.max(state.pot || 0, resultPot(state));
  if (qualifyingPot < threshold) return null;

  const bankroll = Math.max(0, player.stack + (player.handBet || 0));
  if (bankroll < 5000 || Math.abs(state.handNumber || 0) % 4 !== 0) return null;
  const id = bankroll >= 100000 ? 'cigar' : bankroll >= 25000 ? 'bottle' : 'drink';
  return { id, pot: qualifyingPot, threshold, bankroll };
}

/** Varied enough to read as thought, but bounded so the table never drags. */
export function aiThinkDelay(random = Math.random, reducedMotion = false) {
  if (reducedMotion) return 650;
  const roll = Math.min(1, Math.max(0, Number(random()) || 0));
  return 1100 + Math.floor(roll * 700);
}

/** Hold the chosen move on the actor before the next player begins thinking. */
export function aiActionHold(type, reducedMotion = false) {
  if (reducedMotion) return 300;
  return ['BET', 'RAISE', 'ALL_IN'].includes(type) ? 650 : 450;
}

const STREETS = [
  { id: 'preflop', label: 'Pre-flop', threshold: 0 },
  { id: 'flop', label: 'Flop', threshold: 3 },
  { id: 'turn', label: 'Turn', threshold: 4 },
  { id: 'river', label: 'River', threshold: 5 }
];

/**
 * Keeps the board copy driven by cards actually on the felt, rather than by a
 * transient actor change. This also makes a restored hand announce the right
 * street before any player acts.
 */
export function streetPresentation(state) {
  const count = Math.min(5, state.communityCards?.length || 0);
  const currentIndex = count >= 5 ? 3 : count >= 4 ? 2 : count >= 3 ? 1 : 0;
  const current = STREETS[currentIndex];
  const phase = String(state.phase || '').toLowerCase();
  const isShowdown = phase === 'showdown';
  return {
    ...current,
    count,
    headline: isShowdown ? 'Showdown' : current.label,
    detail: isShowdown
      ? 'The strongest five cards decide the pot'
      : current.id === 'preflop'
        ? 'Two cards down • betting before the flop'
        : current.id === 'flop'
          ? 'Three community cards • betting round'
          : current.id === 'turn'
            ? 'Fourth community card • betting round'
            : 'Fifth and final community card • final betting',
    stages: STREETS.map((street, index) => ({
      ...street,
      state: index < currentIndex || isShowdown ? 'complete' : index === currentIndex ? 'current' : 'pending'
    }))
  };
}

/** Return only a made hand's exact five cards. High-card hands intentionally
 * receive no guide treatment so the table never looks needlessly busy. */
export function handGuideCards(evaluation) {
  if (!evaluation || Number(evaluation.category) <= 0) return [];
  const cards = evaluation.bestFive || evaluation.cards || evaluation.best?.cards || [];
  return [...new Set(cards.map(card => typeof card === 'string' ? card : card?.card || card?.code || card?.id).filter(Boolean))].slice(0, 5);
}
